'use client';

import { Box, Card, CardContent, SxProps, Theme } from '@mui/material';
import React, { useEffect } from 'react';

interface VideoCardProps {
  videoUrl: string | null;
  sx?: SxProps<Theme>;
}

const isVimeoUrl = (url: string): boolean => {
  return url.includes('vimeo.com');
};

const VideoSliderItem: React.FC<VideoCardProps> = ({ videoUrl, sx }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      // Pause the video when the tab is not in focus
      const unobserve = autoPauseOnBackground(video);

      // Cleanup function to unobserve the video element
      return () => {
        unobserve();
      };
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      // Pause the video when it is not in view
      const unobserve = autoPauseOnHidden(video);

      // Cleanup function to unobserve the video element
      return () => {
        unobserve();
      };
    }
  }, []);

  return (
    <Card sx={sx} className="video-slider-card">
      <CardContent sx={{ margin: '0', padding: '0', paddingBottom: '2.5rem !important' }}>
        <Box className="responsive-video">
          {videoUrl !== null &&
            (isVimeoUrl(videoUrl) ? (
              <iframe title="vimeo-player" src={videoUrl} allowFullScreen></iframe>
            ) : (
              <video ref={videoRef} controls style={{ width: '100%', height: 'auto' }}>
                <source src={videoUrl} type="video/mp4" />
              </video>
            ))}
        </Box>
      </CardContent>
    </Card>
  );
};

function autoPauseOnHidden(video: HTMLVideoElement) {
  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const v = entry.target as HTMLVideoElement;

        if (!entry.isIntersecting) {
          v.pause();
        }
      }
    },
    { threshold: 0.25 }
  );

  observer.observe(video);
  return () => {
    observer.unobserve(video);
  };
}

function autoPauseOnBackground(video: HTMLVideoElement) {
  function handleVisibilityChange() {
    if (document.hidden) {
      video.pause();
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

export default VideoSliderItem;
